/**
 * Sender avatar utilities: company favicon lookup, deterministic initials, and background color.
 * For company senders (non-personal domains), uses Google's favicon service.
 * For personal email providers, uses the stored Contact entity avatarUrl (populated from Gravatar
 * during contact discovery), falling back to a backend proxy for emails without a contact yet.
 */

import CryptoJS from 'crypto-js';

// Module-level cache: email (lowercase) → avatarUrl from Contact entity skeleton.
// Populated by populateContactAvatarCache() called from the entity store subscription.
const _contactAvatarCache = new Map<string, string>();

/** Sync the cache from the current set of Contact entities in the entity store. */
export function populateContactAvatarCache(
  contacts: Array<{ skeleton: { email?: string; avatarUrl?: string } }>
): void {
  for (const c of contacts) {
    const email = c.skeleton.email?.trim().toLowerCase();
    const url = c.skeleton.avatarUrl?.trim();
    if (email && url) {
      _contactAvatarCache.set(email, url);
    }
  }
}

/** Look up a stored Contact avatar URL by email (O(1)). Returns null if not found. */
export function getStoredContactAvatarUrl(email: string): string | null {
  return _contactAvatarCache.get(email.trim().toLowerCase()) ?? null;
}

export interface SenderAvatarInput {
  name?: string;
  email: string;
  imageUrl?: string;
}

export interface SenderAvatarOptions {
  imageUrl: string | null;
  initials: string;
  color: string;
}

const PERSONAL_EMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com',
  'yahoo.com', 'yahoo.co.uk', 'yahoo.co.in', 'yahoo.fr', 'yahoo.de', 'ymail.com',
  'hotmail.com', 'hotmail.co.uk', 'hotmail.fr', 'hotmail.de',
  'outlook.com', 'outlook.fr', 'live.com', 'msn.com',
  'icloud.com', 'me.com', 'mac.com',
  'aol.com',
  'protonmail.com', 'proton.me',
  'fastmail.com', 'fastmail.fm',
  'zoho.com',
  'mail.com', 'gmx.com', 'gmx.net',
  'inbox.com',
  'yandex.com', 'yandex.ru',
  'tutanota.com', 'tuta.io',
]);

function md5(str: string): string {
  return CryptoJS.MD5(str).toString();
}

/**
 * Get 1–2 letter initials from name and/or email.
 * Prefers name: "First Last" -> "FL", "First" -> "F", "first.last@domain.com" -> "FL".
 * Fallback: first character of email local part, or "?".
 */
export function getInitials(name?: string, email?: string): string {
  const trimmedName = name?.trim();
  if (trimmedName) {
    const parts = trimmedName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      const first = parts[0].charAt(0);
      const last = parts[parts.length - 1].charAt(0);
      if (first && last) return (first + last).toUpperCase();
    }
    if (parts[0]) return parts[0].charAt(0).toUpperCase();
  }
  if (email) {
    const local = email.split('@')[0]?.trim() || '';
    if (local.includes('.')) {
      const segs = local.split('.');
      const a = segs[0]?.charAt(0);
      const b = segs[segs.length - 1]?.charAt(0);
      if (a && b) return (a + b).toUpperCase();
    }
    if (local) return local.charAt(0).toUpperCase();
  }
  return '?';
}

/**
 * Deterministic HSL background color from email (stable per sender).
 */
export function getAvatarColor(email: string): string {
  if (!email?.trim()) return 'hsl(0, 0%, 45%)';
  const hash = md5(email.trim().toLowerCase());
  const hue = parseInt(hash.slice(0, 8), 16) % 360;
  const saturation = 55;
  const lightness = 42;
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

// Known two-part TLD second levels (e.g. co.uk, com.au) so we take 3 parts instead of 2.
const MULTI_PART_TLDS = new Set([
  'co.uk', 'co.jp', 'co.nz', 'co.za', 'co.in', 'co.kr', 'co.il', 'co.id',
  'com.au', 'com.br', 'com.mx', 'com.sg', 'com.ar', 'com.pe', 'com.co', 'com.ng',
  'org.uk', 'net.uk', 'gov.uk', 'ac.uk', 'me.uk',
  'ne.jp', 'or.jp', 'go.jp', 'ac.jp',
]);

/**
 * Extracts the registrable root domain (eTLD+1) from a full domain.
 * e.g. "rivereditor.on.crisp.email" → "crisp.email"
 *      "mail.stripe.com"            → "stripe.com"
 *      "foo.bar.co.uk"              → "bar.co.uk"
 */
function getRootDomain(domain: string): string {
  const parts = domain.split('.');
  if (parts.length <= 2) return domain;
  const twoPartTld = parts.slice(-2).join('.');
  if (MULTI_PART_TLDS.has(twoPartTld)) {
    return parts.slice(-3).join('.');
  }
  return parts.slice(-2).join('.');
}

/**
 * Returns the email domain if it's a genuine company domain suitable for a favicon lookup.
 * Returns null for personal email providers.
 */
export function getCompanyDomain(email: string): string | null {
  const domain = email.split('@')[1]?.toLowerCase().trim();
  if (!domain) return null;
  if (PERSONAL_EMAIL_DOMAINS.has(domain)) return null;
  return domain;
}

/**
 * Returns a favicon URL for company senders via Google's favicon service, using the
 * root domain so subdomains like "rivereditor.on.crisp.email" resolve to "crisp.email".
 * Returns null for personal email providers.
 */
export function getCompanyFaviconUrl(email: string, size: number = 64): string | null {
  const domain = getCompanyDomain(email);
  if (!domain) return null;
  const rootDomain = getRootDomain(domain);
  return `https://www.google.com/s2/favicons?domain=${rootDomain}&sz=${size}`;
}

/**
 * Returns the backend-proxied Gravatar URL for personal email senders.
 * The backend fetches Gravatar server-side and returns 204 if no photo exists,
 * so the browser never sees a 404.
 */
export function getPersonalAvatarUrl(email: string): string {
  return `/api/mail/sender-avatar?email=${encodeURIComponent(email)}`;
}

/**
 * Full options for rendering a sender avatar:
 *   explicit imageUrl
 *   > stored Contact avatarUrl (populated from Gravatar during contact discovery)
 *   > company favicon (company domains)
 *   > proxied Gravatar (personal domains, for emails not yet in contacts)
 *   > initials + color
 */
export function getSenderAvatarOptions(input: SenderAvatarInput): SenderAvatarOptions {
  const { name, email, imageUrl } = input;
  const normalizedEmail = email?.trim() || '';
  const initials = getInitials(name, normalizedEmail);
  const color = getAvatarColor(normalizedEmail);

  let url: string | null = imageUrl?.trim() || null;
  if (!url && normalizedEmail) {
    url =
      getStoredContactAvatarUrl(normalizedEmail) ??
      getCompanyFaviconUrl(normalizedEmail) ??
      getPersonalAvatarUrl(normalizedEmail);
  }

  return {
    imageUrl: url || null,
    initials,
    color,
  };
}
